import { useEffect, useState } from 'react';
import { Package, Image, HelpCircle, Star, User, Code, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { getPackages, getMedia, getFAQs, getReviews, getWidgets } from '../../lib/api';

const StatCard = ({ icon: Icon, label, count, link }) => (
  <Link 
    to={link}
    className="admin-card flex items-center gap-4 hover:shadow-md transition-shadow"
    data-testid={`stat-card-${label.toLowerCase()}`}
  >
    <div className="w-12 h-12 bg-neutral-100 rounded-lg flex items-center justify-center">
      <Icon className="w-6 h-6 text-neutral-700" />
    </div>
    <div>
      <p className="text-2xl font-bold text-neutral-900">{count}</p>
      <p className="text-neutral-500">{label}</p>
    </div>
  </Link>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    packages: 0,
    media: 0,
    faqs: 0,
    reviews: 0,
    widgets: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [packagesRes, mediaRes, faqsRes, reviewsRes, widgetsRes] = await Promise.all([
          getPackages(),
          getMedia(),
          getFAQs(),
          getReviews(),
          getWidgets()
        ]);
        
        setStats({
          packages: packagesRes.data.length,
          media: mediaRes.data.length,
          faqs: faqsRes.data.length,
          reviews: reviewsRes.data.length,
          widgets: widgetsRes.data.filter(w => w.is_active && w.code).length
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-neutral-500 mt-2">Welcome to your admin panel</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <StatCard icon={Package} label="Packages" count={stats.packages} link="/admin/packages" />
              <StatCard icon={Image} label="Media Items" count={stats.media} link="/admin/media" />
              <StatCard icon={HelpCircle} label="FAQs" count={stats.faqs} link="/admin/faqs" />
              <StatCard icon={Star} label="Reviews" count={stats.reviews} link="/admin/reviews" />
              <StatCard icon={Code} label="Active Widgets" count={stats.widgets} link="/admin/widgets" />
            </div>

            {/* Quick Actions */}
            <div className="admin-card">
              <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                Quick Actions
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Link 
                  to="/admin/packages" 
                  className="p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-center"
                  data-testid="quick-action-packages"
                >
                  <Package className="w-6 h-6 mx-auto mb-2 text-neutral-700" />
                  <span className="text-sm font-medium">Add Package</span>
                </Link>
                <Link 
                  to="/admin/media" 
                  className="p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-center"
                  data-testid="quick-action-media"
                >
                  <Image className="w-6 h-6 mx-auto mb-2 text-neutral-700" />
                  <span className="text-sm font-medium">Upload Media</span>
                </Link>
                <Link 
                  to="/admin/widgets" 
                  className="p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-center"
                  data-testid="quick-action-widgets"
                >
                  <Code className="w-6 h-6 mx-auto mb-2 text-neutral-700" />
                  <span className="text-sm font-medium">Edit Widgets</span>
                </Link>
                <Link 
                  to="/admin/settings" 
                  className="p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-center"
                  data-testid="quick-action-settings"
                >
                  <Settings className="w-6 h-6 mx-auto mb-2 text-neutral-700" />
                  <span className="text-sm font-medium">Site Settings</span>
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
